import ReactDiffViewer, { DiffMethod } from "react-diff-viewer";

import { Template } from "@/types";
import { PromptVersion } from "@/types/apiGetters";

interface MetadataDiffProps {
  sourceVersion: PromptVersion;
  targetVersion: PromptVersion;
}

interface MetadataRowProps {
  label: string;
  sourceValue: string | undefined | null;
  targetValue: string | undefined | null;
}

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  return String(value);
};

// Drop _-prefixed enrichment fields so diff doesn't flag them as "fields removed" on save.
const stripEnrichmentFields = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(stripEnrichmentFields);
  }
  if (value !== null && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      if (key.startsWith("_")) {
        continue;
      }
      out[key] = stripEnrichmentFields(val);
    }
    return out;
  }
  return value;
};

const sortKeysDeep = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(sortKeysDeep);
  }
  if (value !== null && typeof value === "object") {
    const sorted: Record<string, unknown> = {};
    Object.keys(value as Record<string, unknown>)
      .sort()
      .forEach(key => {
        sorted[key] = sortKeysDeep((value as Record<string, unknown>)[key]);
      });
    return sorted;
  }
  return value;
};

const stringify = (value: unknown): string =>
  JSON.stringify(sortKeysDeep(stripEnrichmentFields(value ?? null)), null, 2);

const MetadataRow = ({ label, sourceValue, targetValue }: MetadataRowProps) => {
  const source = sourceValue ?? "—";
  const target = targetValue ?? "—";
  if (source === target) {
    return null;
  }

  return (
    <div className="flex items-center gap-3 text-sm">
      <span className="min-w-[120px] font-medium text-gray-500">{label}</span>
      <span className="rounded bg-red-50 px-2 py-0.5 text-red-700 line-through">{source}</span>
      <span className="text-gray-400">→</span>
      <span className="rounded bg-green-50 px-2 py-0.5 text-green-700">{target}</span>
    </div>
  );
};

interface JsonSectionDiffProps {
  label: string;
  sourceValue: unknown;
  targetValue: unknown;
}

const JsonSectionDiff = ({ label, sourceValue, targetValue }: JsonSectionDiffProps) => {
  const sourceStr = stringify(sourceValue);
  const targetStr = stringify(targetValue);
  if (sourceStr === targetStr) {
    return null;
  }

  return (
    <div className="mt-2">
      <h4 className="mb-1 text-xs font-medium text-gray-500">{label}</h4>
      <div className="overflow-x-auto rounded border border-gray-200 bg-white">
        <ReactDiffViewer
          oldValue={sourceStr}
          newValue={targetStr}
          splitView
          hideLineNumbers
          showDiffOnly
          extraLinesSurroundingDiff={2}
          compareMethod={DiffMethod.WORDS}
        />
      </div>
    </div>
  );
};

const getParameterRows = (sourceVersion: PromptVersion, targetVersion: PromptVersion) => {
  const sourceParams = sourceVersion.metadata?.model?.parameters ?? {};
  const targetParams = targetVersion.metadata?.model?.parameters ?? {};
  const allKeys = new Set([...Object.keys(sourceParams), ...Object.keys(targetParams)]);

  const rows: { label: string; sourceValue: string; targetValue: string }[] = [];
  allKeys.forEach(key => {
    const sourceVal = formatValue(sourceParams[key]);
    const targetVal = formatValue(targetParams[key]);
    if (sourceVal !== targetVal) {
      rows.push({ label: key, sourceValue: sourceVal, targetValue: targetVal });
    }
  });
  return rows;
};

const getTemplateField = (template: Template, field: string): unknown => {
  if (template.type === "chat") {
    return template[field as keyof typeof template] ?? null;
  }
  return null;
};

const getJsonSections = (sourceVersion: PromptVersion, targetVersion: PromptVersion) => {
  const fields = [
    { key: "tools", label: "Tools" },
    { key: "functions", label: "Functions" },
    { key: "tool_choice", label: "Tool Choice" },
    { key: "json_schema", label: "Structured Output (JSON Schema)" },
  ];

  return fields.filter(({ key }) => {
    const sourceVal = stringify(getTemplateField(sourceVersion.prompt_template, key));
    const targetVal = stringify(getTemplateField(targetVersion.prompt_template, key));
    return sourceVal !== targetVal;
  });
};

type ModelDisplayVersion = Pick<PromptVersion, "inference_client_name" | "metadata">;

export const getModelDisplayValue = (version: ModelDisplayVersion) => {
  const model = version.metadata?.model;
  if (model?.provider === "huggingface") {
    return version.inference_client_name || model.model_config_display_name || model.name;
  }

  return model?.model_config_display_name || model?.name;
};

const MetadataDiff = ({ sourceVersion, targetVersion }: MetadataDiffProps) => {
  const staticRows = [
    {
      label: "Model",
      sourceValue: getModelDisplayValue(sourceVersion),
      targetValue: getModelDisplayValue(targetVersion),
    },
    {
      label: "Provider",
      sourceValue: sourceVersion.metadata?.model?.provider || sourceVersion.inference_client_name,
      targetValue: targetVersion.metadata?.model?.provider || targetVersion.inference_client_name,
    },
    {
      label: "Base URL",
      sourceValue: sourceVersion.provider_base_url_name,
      targetValue: targetVersion.provider_base_url_name,
    },
  ];

  const parameterRows = getParameterRows(sourceVersion, targetVersion);
  const allRows = [
    ...staticRows.filter(r => (r.sourceValue ?? "—") !== (r.targetValue ?? "—")),
    ...parameterRows,
  ];
  const jsonSections = getJsonSections(sourceVersion, targetVersion);

  if (allRows.length === 0 && jsonSections.length === 0) {
    return null;
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
      <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-gray-500">
        Configuration Changes
      </h3>
      {allRows.length > 0 && (
        <div className="flex flex-col gap-1.5">
          {allRows.map(row => (
            <MetadataRow key={row.label} {...row} />
          ))}
        </div>
      )}
      {jsonSections.map(({ key, label }) => (
        <JsonSectionDiff
          key={key}
          label={label}
          sourceValue={getTemplateField(sourceVersion.prompt_template, key)}
          targetValue={getTemplateField(targetVersion.prompt_template, key)}
        />
      ))}
    </div>
  );
};

export default MetadataDiff;
